import { useTheme, useMediaQuery } from '@mui/material'

/**
 * Responsive breakpoint state based on NN Group recommendations
 *
 * Breakpoints:
 * - Extra-small (xs): 0-499px (mobile, 4-column grid)
 * - Small (sm): 500-1199px (tablet, 8-column grid)
 * - Medium (md): 1200-1399px (laptop, 12-column grid)
 * - Large (lg): 1400px+ (large monitors, 12-column grid)
 */
export interface ResponsiveState {
  /** < 500px - Mobile devices (4-column grid) */
  isMobile: boolean
  /** 500-1199px - Tablet devices (8-column grid) */
  isTablet: boolean
  /** 1200-1399px - Laptop screens (12-column grid) */
  isLaptop: boolean
  /** >= 1400px - Large desktop monitors (12-column grid) */
  isDesktop: boolean
  /** >= 1920px - Ultra-wide displays */
  isUltraWide: boolean
  /** Should show bottom navigation */
  showBottomNav: boolean
  /** Should show permanent drawer */
  showPermanentDrawer: boolean
  /** Should show temporary drawer (tablet with menu button) */
  showTemporaryDrawer: boolean
  /** Landscape orientation */
  isLandscape: boolean
  /** Current breakpoint name for debugging */
  breakpoint: 'mobile' | 'tablet' | 'laptop' | 'desktop' | 'ultrawide'
  /** Grid columns for current breakpoint */
  gridColumns: 4 | 8 | 12
}

/**
 * Centralized responsive breakpoint hook
 * Ensures consistent behavior across all components
 *
 * @example
 * ```tsx
 * const { isMobile, isTablet, showBottomNav, gridColumns } = useResponsive()
 *
 * return (
 *   <Grid container spacing={isMobile ? 0.5 : 3}>
 *     <Grid item xs={12 / gridColumns * 2}>
 *       <Card />
 *     </Grid>
 *   </Grid>
 * )
 * ```
 */
export const useResponsive = (): ResponsiveState => {
  const theme = useTheme()

  // NN Group breakpoint ranges
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) // < 500px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')) // 500-1199px
  const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'lg')) // 1200-1399px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')) // >= 1400px
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xl')) // >= 1920px

  const isLandscape = useMediaQuery('(orientation: landscape)')

  // Determine breakpoint name
  let breakpoint: ResponsiveState['breakpoint']
  if (isMobile) breakpoint = 'mobile'
  else if (isTablet) breakpoint = 'tablet'
  else if (isLaptop) breakpoint = 'laptop'
  else if (isUltraWide) breakpoint = 'ultrawide'
  else breakpoint = 'desktop'

  // Determine grid columns based on NN Group recommendations
  let gridColumns: ResponsiveState['gridColumns']
  if (isMobile)
    gridColumns = 4 // Mobile: 4 columns
  else if (isTablet)
    gridColumns = 8 // Tablet: 8 columns
  else gridColumns = 12 // Laptop/Desktop: 12 columns

  // Navigation behavior
  // Mobile: bottom nav only
  // Tablet: bottom nav (or temporary drawer option)
  // Laptop+: permanent drawer
  const showBottomNav = isMobile || isTablet
  const showPermanentDrawer = isLaptop || isDesktop
  const showTemporaryDrawer = isTablet

  return {
    isMobile,
    isTablet,
    isLaptop,
    isDesktop,
    isUltraWide,
    showBottomNav,
    showPermanentDrawer,
    showTemporaryDrawer,
    isLandscape,
    breakpoint,
    gridColumns,
  }
}

/**
 * Helper hook for common responsive spacing values
 * Returns spacing multipliers based on current breakpoint
 */
export const useResponsiveSpacing = () => {
  const { isMobile, isTablet, isLaptop } = useResponsive()

  return {
    /** Content padding: 12px mobile, 20px tablet, 24px laptop, 32px desktop */
    contentPadding: isMobile ? 1.5 : isTablet ? 2.5 : isLaptop ? 3 : 4,
    /** Card padding: 12px mobile, 16px tablet, 20px laptop, 24px desktop */
    cardPadding: isMobile ? 1.5 : isTablet ? 2 : isLaptop ? 2.5 : 3,
    /** Grid spacing: 4px mobile, 16px tablet, 24px laptop/desktop */
    gridSpacing: isMobile ? 0.5 : isTablet ? 2 : 3,
    /** Section margin bottom: 16px mobile, 24px tablet, 32px laptop/desktop */
    sectionMargin: isMobile ? 2 : isTablet ? 3 : 4,
  }
}
